import React, { useEffect, useState } from 'react';
import MainLayout from '../components/Layout/MainLayout.js';
import FilterBar from '../components/FilterBar.js';
import BlockedOverlay from '../components/BlockedOverlay.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { TrendingUp, TrendingDown, Users, Building2, DollarSign, Shield } from 'lucide-react';
import { useFilters } from '../context/FilterContext.js';
import CompetitivenessIndex from '../components/CompetitivenessIndex.js';

const MetricCard = ({ title, value, subtitle, icon: Icon, trend }) => (
  <Card className="hover:shadow-md transition-shadow" data-testid={`metric-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-slate-600">{title}</CardTitle>
      <Icon className="w-5 h-5 text-slate-400" />
    </CardHeader>
    <CardContent>
      <div className="text-3xl font-bold text-slate-900" data-testid="metric-value">{value}</div>
      {subtitle && (
        <div className={`flex items-center gap-1 mt-2 text-sm ${trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-500' : 'text-slate-500'}`}>
          {trend === 'up' ? <TrendingUp className="w-4 h-4" /> : trend === 'down' ? <TrendingDown className="w-4 h-4" /> : null}
          <span>{subtitle}</span>
        </div>
      )}
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [user, setUser] = useState(null);
  const { fetchWithFilters, filters } = useFilters();
  const userRole = localStorage.getItem('userRole') || 'respondent';

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchWithFilters('/dashboard/stats');
        if (data) {
          setStats(data);
          if (data.user) setUser(data.user);
        }
      } catch (e) { console.error(e); }
    };
    load();
  }, [filters, fetchWithFilters]);

  const blocked = stats?.blocked;

  return (
    <MainLayout title="Dashboard" userRole={userRole} user={user}>
      <div className="space-y-6" data-testid="dashboard-content">
        <FilterBar n={stats?.n || stats?.totalCompanies} blocked={blocked} />

        {blocked ? (
          <BlockedOverlay n={stats?.n} />
        ) : stats ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="metrics-grid">
              <MetricCard title="Empresas" value={stats.totalCompanies} subtitle="participantes" icon={Building2} />
              <MetricCard title="Colaboradores" value={stats.totalEmployees.toLocaleString('pt-BR')} subtitle="total na base" icon={Users} />
              <MetricCard title="Salário Médio" value={`R$ ${stats.avgSalary.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`} subtitle="base + variável" icon={DollarSign} trend="up" />
              <MetricCard title="PCD" value={`${stats.pcdPercentage.toFixed(1)}%`} subtitle="meta legal: 5%" icon={Shield} trend={stats.pcdPercentage >= 5 ? 'up' : 'down'} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CompetitivenessIndex variant="full" />
              <Card data-testid="quick-actions-card">
                <CardHeader><CardTitle className="text-base">Módulos</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {[
                    { label: 'Indicadores RH', desc: 'Turnover, absenteísmo, recrutamento', href: '/indicadores-rh' },
                    { label: 'ESG & Pessoas', desc: 'Diversidade, inclusão, scores ESG', href: '/esg-pessoas' },
                    { label: 'Remuneração', desc: 'Pesquisa salarial por cargo', href: '/remuneracao' },
                    { label: 'Benefícios', desc: 'Comparativo de benefícios', href: '/beneficios' },
                  ].map(m => (
                    <a key={m.href} href={m.href} className="block px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors" data-testid={`module-link-${m.href.slice(1)}`}>
                      <p className="font-medium text-slate-900 text-sm">{m.label}</p>
                      <p className="text-xs text-slate-500">{m.desc}</p>
                    </a>
                  ))}
                </CardContent>
              </Card>
              <Card data-testid="info-card">
                <CardHeader><CardTitle className="text-base">Sobre a Base</CardTitle></CardHeader>
                <CardContent className="text-sm text-slate-600 space-y-3">
                  <p>A plataforma <strong>g2statistics</strong> agrega dados de <strong>{stats.totalCompanies}</strong> empresas para benchmarking de indicadores de RH, ESG e remuneração.</p>
                  <p>Todos os dados são apresentados de forma agregada, respeitando a <strong>governança estatística</strong> (mínimo n=5 empresas).</p>
                  <p className="text-xs text-slate-400">Dados mock para demonstração.</p>
                </CardContent>
              </Card>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center py-20 text-slate-400">Carregando...</div>
        )}
      </div>
    </MainLayout>
  );
};

export default Dashboard;
